/**
 * Modal — 跨端 Dialog primitive
 *
 * 載入：由 /src/main.js 與 /src/admin.js 兩條中央 import 鏈皆載入（前台 + 後台共用）；
 *   宣告式 [data-modal-trigger] auto-init，另掛 window.Modal 供 console / 其他模組程式式呼叫。
 *
 * 功能：
 *   - open / close API
 *   - ESC 關閉
 *   - 點擊 backdrop / [data-modal-dismiss] 關閉
 *   - focus-trap（Tab / Shift+Tab 循環在 panel 內）
 *   - 焦點返還觸發元素
 *   - body 滾動鎖（套 <html>.modal-open）
 *   - aria-hidden 同步
 *
 * 使用方式：
 *
 *   宣告式（auto-init）：
 *     <button data-modal-trigger="#confirm-modal">開啟</button>
 *     <div class="modal" id="confirm-modal" role="dialog" aria-modal="true"
 *          aria-labelledby="confirm-modal-title" aria-hidden="true">
 *       <div class="modal__backdrop" data-modal-dismiss></div>
 *       <div class="modal__panel modal__panel--md">
 *         <header class="modal__header">
 *           <h2 id="confirm-modal-title" class="modal__title">標題</h2>
 *           <button class="modal__close" type="button" aria-label="關閉" data-modal-dismiss>×</button>
 *         </header>
 *         <div class="modal__body">內容</div>
 *         <footer class="modal__footer">
 *           <button class="button button--ghost" data-modal-dismiss>取消</button>
 *           <button class="button button--primary">確認</button>
 *         </footer>
 *       </div>
 *     </div>
 *
 *   程式式 API：
 *     import { Modal } from './components/modal.js';
 *     const m = Modal.open('#my-modal');
 *     m.on('close', () => { ... });
 *     m.close();
 */

// focus-trap selector + getFocusable + Tab 循環 → 收斂至 utils/focus-trap.js（單一真相源）。
// 本檔的 6-token selector + keep-active-el-if-hidden filter 即 canonical 基準（逐字搬入 util，行為等價）。
// trapTab 額外帶「焦點逃出 panel 即拉回」逃逸復原守衛（superset；本檔原 inline 版無此守衛，
// 屬 a11y 強化：焦點若逸出 panel，下一次 Tab 會拉回，不再放行至背景頁面）。
import { getFocusable, trapTab } from '../utils/focus-trap.js';
// P-H-3：改 import 共用 prefersReducedMotion()（utils/dom.js），去重原 inline matchMedia 判斷。
import { prefersReducedMotion } from '../utils/dom.js';

const instances = new WeakMap(); // element -> ModalInstance
let openStack = []; // 支援嵌套（最上層接管 ESC）

class ModalInstance {
  constructor(el) {
    this.el = el;
    this.panel = el.querySelector('.modal__panel');
    this.listeners = { open: [], close: [] };
    this.previousFocus = null;
    this._keydownHandler = this._onKeydown.bind(this);
    this._dismissHandler = this._onDismissClick.bind(this);
  }

  on(event, fn) {
    if (this.listeners[event]) this.listeners[event].push(fn);
    return this;
  }

  _emit(event) {
    (this.listeners[event] || []).forEach((fn) => {
      try { fn(this); } catch (err) { console.error('[modal]', event, err); }
    });
  }

  open() {
    if (this.el.classList.contains('is-open')) return this;

    this.previousFocus = document.activeElement;

    // body lock（多層 modal 共用同一 class）
    document.documentElement.classList.add('modal-open');

    this.el.classList.add('is-open');
    this.el.setAttribute('aria-hidden', 'false');

    // 綁事件
    document.addEventListener('keydown', this._keydownHandler);
    this.el.addEventListener('click', this._dismissHandler);

    openStack.push(this);

    // 對焦到第一個可聚焦元素（panel 自身用 tabindex="-1" fallback）
    const focusables = getFocusable(this.panel);
    const focusTarget = focusables[0] || this.panel;
    if (focusTarget === this.panel && !this.panel.hasAttribute('tabindex')) {
      this.panel.setAttribute('tabindex', '-1');
    }
    // 等動畫進場（與 CSS --motion-base 220ms 對齊）
    const prm = prefersReducedMotion();
    setTimeout(() => focusTarget.focus(), prm ? 0 : 60);

    this._emit('open');
    return this;
  }

  close() {
    if (!this.el.classList.contains('is-open')) return this;

    this.el.classList.remove('is-open');
    this.el.setAttribute('aria-hidden', 'true');

    document.removeEventListener('keydown', this._keydownHandler);
    this.el.removeEventListener('click', this._dismissHandler);

    openStack = openStack.filter((m) => m !== this);

    // 沒有其他 open modal 才解鎖 body
    if (openStack.length === 0) {
      document.documentElement.classList.remove('modal-open');
    }

    // 焦點返還
    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
    }
    this.previousFocus = null;

    this._emit('close');
    return this;
  }

  _onKeydown(e) {
    // 只處理 stack 最上層 modal 的事件
    if (openStack[openStack.length - 1] !== this) return;

    if (e.key === 'Escape') {
      e.stopPropagation();
      this.close();
      return;
    }

    if (e.key === 'Tab') {
      trapTab(e, this.panel);
    }
  }

  _onDismissClick(e) {
    const dismissEl = e.target.closest('[data-modal-dismiss]');
    if (dismissEl && this.el.contains(dismissEl)) {
      this.close();
    }
  }
}

function getOrCreate(target) {
  const el = typeof target === 'string' ? document.querySelector(target) : target;
  if (!el) {
    console.warn('[modal] target not found:', target);
    return null;
  }
  let inst = instances.get(el);
  if (!inst) {
    inst = new ModalInstance(el);
    instances.set(el, inst);
  }
  return inst;
}

export const Modal = {
  open(target) {
    const inst = getOrCreate(target);
    return inst ? inst.open() : null;
  },
  close(target) {
    const inst = getOrCreate(target);
    return inst ? inst.close() : null;
  },
  get(target) {
    return getOrCreate(target);
  },
};

// ===== Auto-init：宣告式觸發 =====
function bindTriggers(root = document) {
  root.querySelectorAll('[data-modal-trigger]').forEach((trigger) => {
    if (trigger.dataset.modalBound === '1') return;
    trigger.dataset.modalBound = '1';
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      const targetSel = trigger.getAttribute('data-modal-trigger');
      Modal.open(targetSel);
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => bindTriggers());
} else {
  bindTriggers();
}

// 暴露給 window 方便 console 測試（不污染 global namespace 太多）
if (typeof window !== 'undefined') {
  window.Modal = Modal;
}
